import { faStar } from '@fortawesome/pro-regular-svg-icons';
import { FavoriteResponse } from '@kcftech/prometheus-client';
import { IconButton } from 'components/button/IconButton';
import { FavoriteButton } from 'components/favorites/FavoriteButton';
import { Tab, TabList, TabPanel, Tabs } from 'components/tabs/Tabs';
import { Menu } from 'components/top_bar/styled';
import { useCreateFavorite } from 'custom_hooks/data_hooks/favorites/useFavorites';
import { useLocationFavorites } from 'custom_hooks/data_hooks/favorites/useLocationFavorites';
import { useSystemFavorites } from 'custom_hooks/data_hooks/favorites/useSystemFavorites';
import { useDashboardFavorites } from 'custom_hooks/data_hooks/useDashboardFavorites';
import { Location, LocationDescriptor } from 'history';
import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { groupBy } from 'utils/array';
import {
    getCorporationSpecificDashboardRoute,
    getLocationLandingPageRoute,
    getLocationSpecificDashboardRoute,
    getSystemWorkbenchTab,
    WorkbenchTab,
} from 'utils/RouteHelper';

const FavoriteTabs = styled(Tabs)``;

const FavoriteTabList = styled(TabList)`
    padding: 1rem 2rem 0 2rem;
`;

const FavoriteTab = styled(Tab)``;

const FavoriteTabPanel = styled(TabPanel)`
    max-height: 34rem;
    overflow-y: auto;
`;

const FavoriteItemWithIcon = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    padding-left: 2rem;
    cursor: pointer;
    text-align: left;

    &:hover {
        background: ${tokens.action.hover};
    }
`;

const FavoriteLink = styled(Link)`
    color: inherit;
    text-decoration: none;
`;

const FavoritesList = styled.ul`
    padding: 0;
    margin: 0;
    list-style: none;
`;

const AddFavorites = styled.div`
    padding: 1.6rem;
    padding-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
`;

export type FavoritesListItemProps = Readonly<{
    favorite: FavoriteResponse;
    favoriteContext: 'corporation' | 'location' | undefined;
    linkTo:
        | LocationDescriptor<unknown>
        | ((location: Location<unknown>) => LocationDescriptor<unknown>);
    locationId?: string;
}> &
    Pick<React.HTMLAttributes<HTMLElement>, 'onClick'>;

const NoFavoritesMessage = ({
    favoriteContext,
    favoriteType,
}: {
    favoriteContext: 'corporation' | 'location' | undefined;
    favoriteType: string;
}) => (
    <p className="p-16 m-0">
        You do not have any {favoriteContext !== undefined && favoriteContext}{' '}
        {favoriteType} favorites yet.
    </p>
);

const FavoritesListItem = ({
    favorite,
    favoriteContext,
    linkTo,
    onClick,
}: FavoritesListItemProps) => (
    <li key={favorite.FavoriteId}>
        <FavoriteLink to={linkTo} onClick={onClick}>
            <FavoriteItemWithIcon
                role="button"
                aria-label={`${favorite.FavoriteType.FavoriteTypeName} Favorite ${favorite.FavoriteName}`}
            >
                <div
                    className="flex-column overflow-hidden"
                    title={favorite.FavoriteName}
                >
                    {favorite.FavoriteType.FavoriteTypeName !== 'Location' &&
                        favoriteContext !== 'location' &&
                        favorite.Location !== undefined && (
                            <>{favorite.Location.LocationName} / </>
                        )}
                    <div className="ellipsis-1-line">{favorite.FavoriteName}</div>
                </div>
                <FavoriteButton
                    favoriteItem={{ Id: favorite.FavoriteId }}
                    favoriteType={favorite.FavoriteType.FavoriteTypeName}
                />
            </FavoriteItemWithIcon>
        </FavoriteLink>
    </li>
);

type FavoriteGroups = {
    dashboard: FavoriteResponse[];
    systems: FavoriteResponse[];
    locations: FavoriteResponse[];
};

export type FavoritesMenuProps = {
    corporationId?: string;
    locationId?: string;
    onClose?: () => void;
};

export function FavoritesMenu({
    corporationId,
    locationId,
    onClose,
}: FavoritesMenuProps): JSX.Element {
    const favoriteGroups: FavoriteGroups = {
        dashboard: useDashboardFavorites({ corporationId, locationId }),
        systems: useSystemFavorites({ corporationId, locationId }),
        locations: useLocationFavorites({ corporationId, locationId }),
    };

    const locationFavoritesByCorporation = Object.entries(
        groupBy(
            favoriteGroups.locations,
            (favorite) => favorite.Corporation?.CorporationName ?? '',
        ),
    );

    const systemFavoritesByCorporation = Object.entries(
        groupBy(
            favoriteGroups.systems,
            (favorite) => favorite.Corporation?.CorporationName ?? '',
        ),
    );

    const dashboardFavoritesByCorporation = Object.entries(
        groupBy(
            favoriteGroups.dashboard,
            (favorite) => favorite.Corporation?.CorporationName ?? '',
        ),
    );

    const favoriteContext = useMemo(() => {
        if (locationId) {
            return 'location';
        }

        if (corporationId) {
            return 'corporation';
        }

        return undefined;
    }, [corporationId, locationId]);

    const {
        mutate: createFavorite,
        isPending: createFavoritePending,
    } = useCreateFavorite();

    return (
        <Menu>
            <FavoriteTabs>
                <FavoriteTabList>
                    <FavoriteTab>Locations</FavoriteTab>
                    <FavoriteTab>Systems</FavoriteTab>
                    <FavoriteTab>Dashboards</FavoriteTab>
                </FavoriteTabList>
                <FavoriteTabPanel>
                    <FavoritesList aria-label="Favorites List">
                        {favoriteGroups.locations.length === 0 && (
                            <>
                                {!locationId && (
                                    <NoFavoritesMessage
                                        favoriteContext={favoriteContext}
                                        favoriteType="location"
                                    />
                                )}
                                {locationId &&
                                    !favoriteGroups.locations.some(
                                        (favorite) => favorite.FavoriteId == locationId,
                                    ) && (
                                        <AddFavorites>
                                            <div>
                                                This location has not been added to
                                                favorites yet.
                                            </div>
                                            <div className="flex align-center justify-center">
                                                <IconButton
                                                    icon={faStar}
                                                    disabled={createFavoritePending}
                                                    onClick={() =>
                                                        createFavorite({
                                                            id: locationId,
                                                            type: 'Location',
                                                        })
                                                    }
                                                >
                                                    Add to favorites
                                                </IconButton>
                                            </div>
                                        </AddFavorites>
                                    )}
                            </>
                        )}
                        {locationFavoritesByCorporation.map(
                            ([corporationName, favorites]) => (
                                <Fragment key={corporationName}>
                                    {favoriteContext === undefined && (
                                        <div className="bold p-12">{corporationName}</div>
                                    )}
                                    {(favorites === undefined ||
                                        favorites.length === 0) && (
                                        <>
                                            {!locationId && (
                                                <NoFavoritesMessage
                                                    favoriteContext={favoriteContext}
                                                    favoriteType="location"
                                                />
                                            )}
                                            {locationId &&
                                                !favorites?.some(
                                                    (favorite) =>
                                                        favorite.FavoriteId == locationId,
                                                ) && (
                                                    <AddFavorites>
                                                        <div>
                                                            This location has not been
                                                            added to favorites yet.
                                                        </div>
                                                        <div className="flex align-center justify-center">
                                                            <IconButton
                                                                icon={faStar}
                                                                disabled={
                                                                    createFavoritePending
                                                                }
                                                                onClick={() =>
                                                                    createFavorite({
                                                                        id: locationId,
                                                                        type: 'Location',
                                                                    })
                                                                }
                                                            >
                                                                Add to favorites
                                                            </IconButton>
                                                        </div>
                                                    </AddFavorites>
                                                )}
                                        </>
                                    )}
                                    {favorites?.map((fav) => (
                                        <FavoritesListItem
                                            key={fav.FavoriteId}
                                            favoriteContext={favoriteContext}
                                            favorite={fav}
                                            linkTo={getLocationLandingPageRoute(
                                                fav.FavoriteId,
                                            )}
                                            onClick={onClose}
                                        />
                                    ))}
                                </Fragment>
                            ),
                        )}
                    </FavoritesList>
                </FavoriteTabPanel>
                <FavoriteTabPanel>
                    <FavoritesList aria-label="Favorites List">
                        {favoriteGroups.systems.length === 0 && (
                            <NoFavoritesMessage
                                favoriteContext={favoriteContext}
                                favoriteType="system"
                            />
                        )}
                        {systemFavoritesByCorporation.map(
                            ([corporationName, favorites]) => (
                                <Fragment key={corporationName}>
                                    {favoriteContext === undefined && (
                                        <div className="bold p-12">{corporationName}</div>
                                    )}
                                    {favorites?.map((fav) => (
                                        <FavoritesListItem
                                            key={fav.FavoriteId}
                                            favoriteContext={favoriteContext}
                                            favorite={fav}
                                            linkTo={getSystemWorkbenchTab(
                                                fav.FavoriteId,
                                                WorkbenchTab.Trend,
                                            )}
                                            onClick={onClose}
                                        />
                                    ))}
                                </Fragment>
                            ),
                        )}
                    </FavoritesList>
                </FavoriteTabPanel>
                <FavoriteTabPanel>
                    <FavoritesList aria-label="Favorites List">
                        {favoriteGroups.dashboard.length === 0 && (
                            <NoFavoritesMessage
                                favoriteContext={favoriteContext}
                                favoriteType="dashboard"
                            />
                        )}
                        {dashboardFavoritesByCorporation.map(
                            ([corporationName, favorites]) => (
                                <Fragment key={corporationName}>
                                    {favoriteContext === undefined && (
                                        <div className="bold p-12">{corporationName}</div>
                                    )}
                                    {favorites?.map((fav) => (
                                        <FavoritesListItem
                                            key={fav.FavoriteId}
                                            favoriteContext={favoriteContext}
                                            favorite={fav}
                                            linkTo={
                                                fav.Location?.LocationId
                                                    ? getLocationSpecificDashboardRoute(
                                                          fav.Location.LocationId,
                                                          fav.FavoriteId,
                                                      )
                                                    : getCorporationSpecificDashboardRoute(
                                                          fav.Corporation?.CorporationId,
                                                          fav.FavoriteId,
                                                      )
                                            }
                                            onClick={onClose}
                                        />
                                    ))}
                                </Fragment>
                            ),
                        )}
                    </FavoritesList>
                </FavoriteTabPanel>
            </FavoriteTabs>
        </Menu>
    );
}
